import React, { FunctionComponent, ReactElement } from 'react';
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
  Theme
} from '@mui/material/styles';
import StylesProvider from '@mui/styles/StylesProvider';
import jssPreset from '@mui/styles/jssPreset';
import { grey } from '@mui/material/colors';
import CssBaseline from '@mui/material/CssBaseline';
import { create } from 'jss';
import rtl from 'jss-rtl';
// Configure JSS
const jss = create({ plugins: [...jssPreset().plugins, rtl()] });
// A theme with custom primary and secondary color.
// It's optional.
export const theme = (dir?: 'ltr' | 'rtl'): Theme =>
  createTheme({
    direction: dir,
    palette: {
      mode: 'light',
      contrastThreshold: 4.5,
      primary: {
        light: '#cddbfe',
        main: '#1f5ff3',
        dark: '#1143ae'
      },
      secondary: {
        light: '#fdc5d7',
        main: '#e9055b',
        dark: '#91054d'
      },
      error: {
        light: '#fcd4d0',
        main: '#d9240f',
        dark: '#a71809'
      },
      warning: {
        light: '#fffad7',
        main: '#fbe637',
        dark: '#917205'
      },
      information: {
        light: '#c6ecfc',
        main: '#37bbfb',
        dark: '#005ba4',
        contrastText: '#333',
        background: '#EBF8FE',
        pageBackground: '#FAFAFA'
      },
      info: {
        light: '#deeeff',
        main: '#93b0d0',
        dark: '#587ca5'
      },
      success: {
        light: '#d2f9ea',
        main: '#26d293',
        dark: '#008454'
      },
      grey: {
        main: grey[300],
        dark: grey[400]
      },
      action: {
        active: 'rgba(0, 0, 0, 0.6)'
      },
      shadow: {
        paper: 'rgba(0,0,0,36%) 0px 0 12px 0px',
        menu: 'rgba(0,0,0,50%) 12px -12px 12px -15px',
        export: 'rgba(0,0,0,0.38) 1px 1px 5px 1px '
      },
      skelton: {
        color: '#bdbdbd'
      },
      text: {
        primary: '#000000DE',
        secondary: 'rgba(0, 0, 0, 0.6)'
      }
    },
    typography: {
      useNextVariants: true,
      fontFamily: 'Roboto, ui-sans-serif, Segoe UI, Arial, sans-serif',
      fontSize: 13,
      h1: {
        fontFamily: 'FinancierDisplay, "Source Serif 4", ui-serif, serif',
        fontWeight: 500
      },
      h2: {
        fontFamily: 'FinancierDisplay, "Source Serif 4", ui-serif, serif',
        fontWeight: 500
      },
      h3: {
        fontFamily: 'FinancierDisplay, "Source Serif 4", ui-serif, serif',
        fontWeight: 500
      },
      h4: {
        fontFamily: 'FinancierDisplay, "Source Serif 4", ui-serif, serif',
        fontWeight: 500
      },
      h5: {
        fontFamily: 'FinancierDisplay, "Source Serif 4", ui-serif, serif',
        fontWeight: 700
      },
      h6: {
        fontFamily: 'FinancierDisplay, "Source Serif 4", ui-serif, serif',
        fontWeight: 700,
        fontSize: 18.57
      },
      subtitle1: {
        fontFamily: 'FinancierDisplay, "Source Serif 4", ui-serif, serif',
        fontWeight: 500
      },
      subtitle2: {
        fontFamily: 'FinancierDisplay, "Source Serif 4", ui-serif, serif',
        fontWeight: 400,
        fontSize: 14.86
      },
      button: {
        fontFamily: 'Roboto, ui-sans-serif, Segoe UI, Arial, sans-serif',
        fontWeight: 500
      }
    },
    components: {
      MuiListItem: {
        defaultProps: {
          dense: true
        }
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
          margin: 'none',
          variant: 'filled'
        }
      },
      MuiFormControl: {
        defaultProps: {
          size: 'small',
          margin: 'none',
          variant: 'filled'
        }
      },
      MuiCheckbox: {
        defaultProps: {
          color: 'primary'
        }
      },
      MuiGrid: {
        defaultProps: {
          spacing: 2
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '8px 16px'
          }
        }
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: '16px'
          }
        }
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            fontSize: '1.5rem'
          },
          fontSizeSmall: {
            fontSize: '1.25rem'
          },
          fontSizeLarge: {
            fontSize: '2.1875rem'
          }
        }
      }
    }
  } as any);
const MuiTheme: FunctionComponent<{
  direction: 'ltr' | 'rtl' | undefined;
  children: ReactElement;
  // eslint-disable-next-line react/prop-types
}> = ({ direction, children }) => {
  return (
    <StylesProvider jss={jss}>
      <CssBaseline />
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme(direction)}>{children}</ThemeProvider>
      </StyledEngineProvider>
    </StylesProvider>
  );
};
export default MuiTheme;
